import React, { useEffect } from "react";
import {
  createBrowserRouter,
  RouterProvider,
  useNavigate,
} from "react-router-dom";

import { Typography } from "@mui/material";
import ManagerHomeView from "../views/ManagerHomeView/ManagerHomeView";
import ManagerSettingsView from "../views/ManagerSettingsView/ManagerSettingsView";
import ManagerLettersView from "../views/ManagerLettersView/ManagerLettersView";
import ManagerTicketView from "../views/ManagerTicketView/ManagerTicketView";
import ManagerTicketsView from "../views/ManagerTicketsView/ManagerTicketsView";
import ManagerCasesView from "../views/ManagerCasesView/ManagerCasesView";
import ManagerUsersView from "../views/ManagerUsersView/ManagerUsersView";
import ManagerUserView from "../views/ManagerUserView/ManagerUserView";
import ManagersView from "../views/ManagersView/ManagersView";
import ManagerCreateTicketView from "../views/ManagerCreateTicketView/ManagerCreateTicketView";
import ManagerCreateUserView from "../views/ManagerCreateUserView/ManagerCreateUserView";
import CreateManagerView from "../views/CreateManagerView/CreateManagerView";
import ManagerCourtsView from "../views/ManagerCourtsView/ManagerCourtsView";
import ManagerCourtView from "../views/ManagerCourtView/ManagerCourtView";
import ManagerCreateCourtView from "../views/ManagerCreateCourtView/ManagerCreateCourtView";
import ManagerCreateProsecutorView from "../views/ManagerCreateProsecutorView/ManagerCreateProsecutorView";
import ManagerCalendarView from "../views/ManagerCalendarView/ManagerCalendarView";
import ManagerBulkNoticesView from "../views/ManagerBulkNoticesView/ManagerBulkNoticesView";
import NotFoundView from "../views/NotFoundView/NotFoundView";
import ManagerView from "../views/ManagerView/ManagerView";
import ManagerPasswordResetView from "../views/ManagerPasswordResetView/ManagerPasswordResetView";
import CaseProgressView from "../views/CaseProgressView/CaseProgressView";
import ManagerMessagesView from "../views/ManagerMessagesView/ManagerMessagesView";
import ManagerMessageView from "../views/ManagerMessageView/ManagerMessageView";
import ManagerAlertsView from "../views/ManagerAlertsView/ManagerAlertsView";
import ManagerProductsView from "../views/ManagerProductsView/ManagerProductsView";
import ManagerProductView from "../views/ManagerProductView/ManagerProductView";
import RevenueDashboardView from "../views/RevenueDashboardView/RevenueDashboardView";
import ManagerPendingAgreementsView from "../views/ManagerPendingAgreementsView/ManagerPendingAgreementsView";
import ManagerOverdueAcknowledgmentsView from "../views/ManagerOverdueAcknowledgmentsView/ManagerOverdueAcknowledgmentsView";
import ManagerAwaitingTrialSettingsView from "../views/ManagerAwaitingTrialSettingsView/ManagerAwaitingTrialSettingsView";
import ManagerExpertDesignationView from "../views/ManagerExpertDesignationView/ManagerExpertDesignationView";
import ManagerCoverageView from "../views/ManagerCoverageView/ManagerCoverageView";
import ManagerStaleCasesView from "../views/ManagerStaleCasesView/ManagerStaleCasesView";
import ManagerCdlRequestsView from "../views/ManagerCdlRequestsView/ManagerCdlRequestsView";
import ManagerReminderDraftsView from "../views/ManagerReminderDraftsView/ManagerReminderDraftsView";
import ManagerLayout from "../components/ManagerLayout/ManagerLayout";
import {
  LegacyAppearanceCalendarRedirect,
  LegacyAppearanceCaseRedirect,
} from "../components/AppearanceAlertRedirects/AppearanceAlertRedirects";

const ManagerAuthRouter = createBrowserRouter(
  [
    {
      path: "/manager-reset-password",
      element: <ManagerPasswordResetView />,
    },
    {
      // Partner case link. Registered in both routers so it opens the same whether or not the
      // visitor happens to have a staff session — a referral partner has no login at all.
      path: "/case/:token",
      element: <CaseProgressView />,
    },
    {
      /*
       * Every signed-in manager page hangs off one layout, so the nav is structural rather than
       * something each view remembers to render. That memory is what failed on /letters (Lloyd,
       * Aug 19) and left a page with no way out of it.
       *
       * The two routes above stay outside deliberately — password reset is reached without a
       * session, and the partner case link belongs to people who have no login at all.
       */
      element: <ManagerLayout />,
      children: [
      {
        path: "/",
        element: <ManagerHomeView />,
      },
      {
        path: "/home",
        element: <ManagerHomeView />,
      },
      {
        path: "/settings",
        element: <ManagerSettingsView />,
      },
      {
        // The letters of representation. Readable by any signed-in manager; publishing is gated on
        // the server by Manager_CanEditLetters.
        path: "/letters",
        element: <ManagerLettersView />,
      },
      {
        path: "/revenue",
        element: <RevenueDashboardView />,
      },
      {
        // Open / closed / all cases, firm-wide. One view; the segment picks the list.
        path: "/cases/:state",
        element: <ManagerCasesView />,
      },
      {
        // Kept reachable so existing bookmarks and links still resolve, but no longer in the menu —
        // the open/closed lists replace it.
        path: "/tickets",
        element: <ManagerTicketsView />,
      },
      {
        path: "/users",
        element: <ManagerUsersView />,
      },
      {
        path: "/user",
        element: <ManagerUserView />,
      },
      {
        path: "/managers",
        element: <ManagersView />,
      },
      {
        path: "/manager",
        element: <ManagerView />,
      },
      {
        path: "/create-manager",
        element: <CreateManagerView />,
      },
      {
        path: "/create-user",
        element: <ManagerCreateUserView />,
      },
      {
        path: "/new-ticket",
        element: <ManagerCreateTicketView />,
      },
      {
        path: "/courts",
        element: <ManagerCourtsView />,
      },
      {
        path: "/create-court",
        element: <ManagerCreateCourtView />,
      },
      {
        path: "/court",
        element: <ManagerCourtView />,
      },
      {
        path: "/add-prosecutor",
        element: <ManagerCreateProsecutorView />,
      },
      {
        path: "/calendar",
        element: <ManagerCalendarView />,
      },
      {
        // Paths the appearance-outcome cron used to write. Not real pages — send them
        // to /ticket and /calendar so an old alert still opens the case.
        path: "/manager/case/:ticketGuid",
        element: <LegacyAppearanceCaseRedirect />,
      },
      {
        path: "/manager/calendar",
        element: <LegacyAppearanceCalendarRedirect />,
      },
      {
        path: "/discovery/overdue-acknowledgments",
        element: <ManagerOverdueAcknowledgmentsView />,
      },
      {
        path: "/discovery/expert-designation-heads-up",
        element: <ManagerExpertDesignationView />,
      },
      {
        path: "/trial/awaiting-settings",
        element: <ManagerAwaitingTrialSettingsView />,
      },
      {
        path: "/covering-attorneys",
        element: <ManagerCoverageView />,
      },
      {
        path: "/stale-cases",
        element: <ManagerStaleCasesView />,
      },
      {
        path: "/cdl-requests",
        element: <ManagerCdlRequestsView />,
      },
      {
        path: "/reminder-drafts",
        element: <ManagerReminderDraftsView />,
      },
      {
        path: "/agreements/pending",
        element: <ManagerPendingAgreementsView />,
      },
      {
        path: "/bulk-notices",
        element: <ManagerBulkNoticesView />,
      },
      {
        path: "/messages",
        element: <ManagerMessagesView />,
      },
      {
        path: "/message",
        element: <ManagerMessageView />,
      },
      {
        path: "/alerts",
        element: <ManagerAlertsView />,
      },
      {
        path: "/products",
        element: <ManagerProductsView />,
      },
      {
        path: "/product",
        element: <ManagerProductView />,
      },
      // {
      //   path: "/ticket/:ticketGuid",
      //   element: <ManagerTicketView />,
      // },
      {
        path: "/ticket",
        element: <ManagerTicketView />,
      },
      {
        path: "*",
        element: <NotFoundView />,
      },
      ],
    },
  ],
  {}
);
export default ManagerAuthRouter;